import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const WAITLIST_FEE = 20000;
const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || '';

// ─── Google Fonts ─────────────────────────────────────────────────────────────
const fontLink = document.createElement('link');
fontLink.rel = 'stylesheet';
fontLink.href = 'https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap';
if (!document.head.querySelector('[href*="DM+Serif"]')) document.head.appendChild(fontLink);

function loadPaystackScript() {
  return new Promise((resolve) => {
    if (window.PaystackPop) return resolve(true);
    const existing = document.querySelector('script[src="https://js.paystack.co/v1/inline.js"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });
}

// ─── Inline styles ────────────────────────────────────────────────────────────
const injectStyles = () => {
  if (document.getElementById('wl-styles')) return;
  const s = document.createElement('style');
  s.id = 'wl-styles';
  s.textContent = `
    .wl-root { font-family: 'DM Sans', sans-serif; }
    .wl-serif { font-family: 'DM Serif Display', serif; }
    @keyframes wl-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
    @keyframes wl-pulse-ring { 0%{transform:scale(1);opacity:1} 100%{transform:scale(2);opacity:0} }
    @keyframes wl-shimmer { 0%{background-position:200% center} 100%{background-position:-200% center} }
    @keyframes wl-fade-up { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
    @keyframes wl-spin { to{transform:rotate(360deg)} }
    .wl-float { animation: wl-float 4s ease-in-out infinite; }
    .wl-fade-up { animation: wl-fade-up 0.6s ease forwards; }
    .wl-fade-up-1 { animation: wl-fade-up 0.6s 0.1s ease both; }
    .wl-fade-up-2 { animation: wl-fade-up 0.6s 0.2s ease both; }
    .wl-fade-up-3 { animation: wl-fade-up 0.6s 0.3s ease both; }
    .wl-fade-up-4 { animation: wl-fade-up 0.6s 0.4s ease both; }
    .wl-shimmer-text {
      background: linear-gradient(90deg, #a5c250, #d4ed7a, #a5c250, #7da83a);
      background-size: 200% auto;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      animation: wl-shimmer 3s linear infinite;
    }
    .wl-card-hover { transition: transform 0.2s ease, box-shadow 0.2s ease; }
    .wl-card-hover:hover { transform: translateY(-3px); box-shadow: 0 12px 40px rgba(41,76,121,0.15); }
    .wl-input {
      width: 100%; padding: 14px 18px;
      border: 2px solid #e8edf5; border-radius: 14px;
      font-family: 'DM Sans', sans-serif; font-size: 15px; color: #1a2540;
      background: #fafbfd; transition: all 0.2s ease; outline: none;
    }
    .wl-input:focus { border-color: #a5c250; background: #fff; box-shadow: 0 0 0 4px rgba(165,194,80,0.12); }
    .wl-input:disabled { background: #f4f6fa; color: #aaa; cursor: not-allowed; }
    .wl-input::placeholder { color: #b0bac9; }
    .wl-btn-primary {
      background: linear-gradient(135deg, #a5c250 0%, #7da83a 100%);
      color: white; border: none; border-radius: 14px;
      font-family: 'DM Sans', sans-serif; font-weight: 700; font-size: 16px;
      cursor: pointer; transition: all 0.2s ease;
      box-shadow: 0 4px 20px rgba(165,194,80,0.35);
    }
    .wl-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(165,194,80,0.45); }
    .wl-btn-primary:active { transform: translateY(0); }
    .wl-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
    .wl-btn-dark {
      background: linear-gradient(135deg, #294c79 0%, #1a3250 100%);
      color: white; border: none; border-radius: 14px;
      font-family: 'DM Sans', sans-serif; font-weight: 700; font-size: 16px;
      cursor: pointer; transition: all 0.2s ease;
      box-shadow: 0 4px 20px rgba(41,76,121,0.3);
    }
    .wl-btn-dark:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(41,76,121,0.4); }
    .wl-btn-dark:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
    .wl-live-dot::before {
      content: ''; display: inline-block;
      width: 8px; height: 8px; border-radius: 50%;
      background: #a5c250; margin-right: 8px; vertical-align: middle;
      box-shadow: 0 0 0 rgba(165,194,80,0.6);
      animation: wl-pulse-ring 1.5s ease-out infinite;
    }
    .wl-faq-answer { max-height: 0; overflow: hidden; transition: max-height 0.35s ease, padding 0.2s ease; }
    .wl-faq-answer.open { max-height: 400px; }
    .wl-step-line { width: 2px; background: linear-gradient(to bottom, #a5c250, transparent); margin: 4px 0 4px 19px; height: 32px; }
    .wl-spin { animation: wl-spin 0.9s linear infinite; }
    .wl-noise {
      position: absolute; inset: 0; pointer-events: none; opacity: 0.04;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
    }
  `;
  document.head.appendChild(s);
};

// ─── Sub-components ───────────────────────────────────────────────────────────
function StatPill({ icon, value, label }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)',
      border: '1px solid rgba(255,255,255,0.15)', borderRadius: 20,
      padding: '18px 20px', textAlign: 'center',
    }}>
      <div style={{ fontSize: 28, marginBottom: 6 }}>{icon}</div>
      <div style={{ color: '#fff', fontWeight: 800, fontSize: 22, lineHeight: 1 }}>{value}</div>
      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 4 }}>{label}</div>
    </div>
  );
}

function FeatureTile({ icon, title, desc }) {
  return (
    <div className="wl-card-hover" style={{
      background: '#fff', borderRadius: 20, padding: '24px 22px',
      border: '1.5px solid #eef2f8',
      boxShadow: '0 2px 16px rgba(41,76,121,0.06)',
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: 14,
        background: 'linear-gradient(135deg, #e8f2d1, #c8dfa0)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 22, marginBottom: 14,
      }}>{icon}</div>
      <div style={{ fontWeight: 700, color: '#1a2540', marginBottom: 6, fontSize: 15 }}>{title}</div>
      <div style={{ color: '#7a8fa6', fontSize: 13.5, lineHeight: 1.65 }}>{desc}</div>
    </div>
  );
}

function StepItem({ num, title, desc, last }) {
  return (
    <div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div style={{
          flexShrink: 0, width: 40, height: 40, borderRadius: '50%',
          background: 'linear-gradient(135deg, #294c79, #1a3250)',
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: 16, boxShadow: '0 4px 14px rgba(41,76,121,0.3)',
        }}>{num}</div>
        <div style={{ paddingTop: 2 }}>
          <div style={{ fontWeight: 700, color: '#1a2540', fontSize: 15, marginBottom: 4 }}>{title}</div>
          <div style={{ color: '#7a8fa6', fontSize: 13.5, lineHeight: 1.65 }}>{desc}</div>
        </div>
      </div>
      {!last && <div className="wl-step-line" />}
    </div>
  );
}

function FAQItem({ q, a, open, toggle }) {
  return (
    <div style={{
      border: '1.5px solid', borderColor: open ? '#a5c250' : '#eef2f8',
      borderRadius: 16, overflow: 'hidden',
      background: open ? '#fafef4' : '#fff',
      transition: 'border-color 0.2s, background 0.2s',
      marginBottom: 10,
    }}>
      <button onClick={toggle} style={{
        width: '100%', padding: '18px 22px', textAlign: 'left',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: 'none', border: 'none', cursor: 'pointer', gap: 16,
        fontFamily: 'DM Sans, sans-serif',
      }}>
        <span style={{ fontWeight: 600, color: '#1a2540', fontSize: 14.5, lineHeight: 1.4 }}>{q}</span>
        <span style={{
          flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
          background: open ? '#a5c250' : '#f0f3f8',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: open ? '#fff' : '#7a8fa6', fontWeight: 700, fontSize: 18,
          transition: 'all 0.2s', transform: open ? 'rotate(45deg)' : 'none',
        }}>+</span>
      </button>
      <div className={`wl-faq-answer ${open ? 'open' : ''}`}>
        <div style={{ padding: '0 22px 18px', color: '#5a6b7e', fontSize: 14, lineHeight: 1.75 }}>{a}</div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function WaitlistPage() {
  injectStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const house = location.state?.house || {};
  const formRef = useRef(null);

  const [currentUser, setCurrentUser] = useState(null);
  const [userToken, setUserToken] = useState(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [guestAlreadyJoined, setGuestAlreadyJoined] = useState(false);
  const [checkingGuest, setCheckingGuest] = useState(false);

  const [form, setForm] = useState({
    fullName: '', phone: '', email: '',
    location: '', moveInDate: '', budget: '', message: '',
  });

  const [payPriorityFee, setPayPriorityFee] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successData, setSuccessData] = useState({});
  const [error, setError] = useState('');
  const [openFAQ, setOpenFAQ] = useState(null);
  const [paystackReady, setPaystackReady] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // ── Auth for header ───────────────────────────────────────
  const { isLoggedIn, logout: authLogout, user } = useContext(AuthContext);
  const adminToken = localStorage.getItem('adminAuthToken');
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const isAdminLoggedIn = !!adminToken && adminUser.role;
  const getDashboardLink = () => isAdminLoggedIn ? '/admin' : '/dashboard';
  const getDashboardLabel = () => {
    if (isAdminLoggedIn) return 'Admin Panel';
    return user?.name ? `Hi, ${user.name.split(' ')[0]}` : 'Dashboard';
  };
  const handleLogout = () => {
    localStorage.removeItem('adminAuthToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    authLogout();
    navigate('/', { replace: true });
    setTimeout(() => window.location.reload(), 100);
  };

  useEffect(() => {
    loadPaystackScript().then(ok => { setPaystackReady(ok); });
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('user') || localStorage.getItem('authUser') || localStorage.getItem('currentUser');
    const token = localStorage.getItem('authToken') || localStorage.getItem('userToken') || localStorage.getItem('token');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setCurrentUser(parsed);
        setForm(prev => ({
          ...prev,
          fullName: parsed.full_name || parsed.fullName || prev.fullName,
          phone: parsed.phone || prev.phone,
          email: parsed.email || prev.email,
        }));
      } catch {}
    }
    if (token) setUserToken(token);
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    const check = async () => {
      setCheckingStatus(true);
      try {
        const token = userToken || localStorage.getItem('authToken') || localStorage.getItem('userToken') || localStorage.getItem('token');
        const params = new URLSearchParams();
        if (house.id) params.append('houseId', house.id);
        const res = await axios.get(`${API_URL}/api/waitlist/check?${params.toString()}`,
          token ? { headers: { Authorization: `Bearer ${token}` } } : {});
        const exists = res.data?.exists === true || res.data?.submitted === true || res.data?.alreadyJoined === true || res.data?.alreadySubmitted === true;
        setAlreadySubmitted(exists);
      } catch { setAlreadySubmitted(false); }
      finally { setCheckingStatus(false); }
    };
    check();
  }, [currentUser, house.id, userToken]);

  useEffect(() => {
    if (house.address || house.location) {
      setForm(prev => ({ ...prev, location: house.address || house.location || prev.location }));
    }
  }, [house.address, house.location]);

  useEffect(() => {
    setForm(prev => ({
      ...prev,
      message: house.title
        ? `I'm really interested in ${house.title} and would love to submit the interest form.`
        : 'I want to be among the first to access LiteRent when it launches.',
    }));
  }, [house.title]);

  const checkGuestDuplicate = useCallback(async (phone, email) => {
    if (currentUser) return;
    if (!phone && !email) return;
    setCheckingGuest(true);
    try {
      const params = new URLSearchParams();
      if (phone) params.append('phone', phone);
      if (email) params.append('email', email);
      if (house.id) params.append('houseId', house.id);
      const res = await axios.get(`${API_URL}/api/waitlist/check?${params.toString()}`);
      const exists = res.data?.exists === true || res.data?.submitted === true || res.data?.alreadyJoined === true;
      if (exists) setGuestAlreadyJoined(true);
    } catch {}
    finally { setCheckingGuest(false); }
  }, [currentUser, house.id]);

  const handleBlurCheck = () => {
    if (!currentUser && (form.phone.length >= 10 || form.email.includes('@'))) {
      checkGuestDuplicate(form.phone, form.email);
    }
  };

  const handleChange = (e) => {
    setGuestAlreadyJoined(false);
    setError('');
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth' });

  const submitToBackend = async ({ paystackRef = null, feePaid = false } = {}) => {
    setLoading(true);
    setError('');
    try {
      const token = userToken || localStorage.getItem('authToken') || localStorage.getItem('userToken') || localStorage.getItem('token');
      const payload = {
        fullName: form.fullName, full_name: form.fullName,
        phone: form.phone, email: form.email,
        location: form.location,
        moveInDate: form.moveInDate, move_in_date: form.moveInDate,
        budget: form.budget, message: form.message,
        houseId: house.id || house._id || null, house_id: house.id || house._id || null,
        houseTitle: house.title || 'General Interest Form', house_title: house.title || 'General Interest Form',
        houseAddress: house.address || house.location || null, house_address: house.address || house.location || null,
        priorityFee: feePaid, priority_fee: feePaid, priorityFeePaid: feePaid,
        paystackReference: paystackRef, paystack_reference: paystackRef,
        feeAmount: feePaid ? WAITLIST_FEE : 0, fee_amount: feePaid ? WAITLIST_FEE : 0,
      };
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API_URL}/api/waitlist`, payload, { headers });
      if (currentUser) setAlreadySubmitted(true);
      setSuccessData({ feePaid, name: form.fullName, phone: form.phone });
      setSuccess(true);
      setTimeout(() => navigate('/'), 5000);
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.response?.data?.error || '';
      if (status === 409 || msg.toLowerCase().includes('already') || msg.toLowerCase().includes('duplicate') || msg.toLowerCase().includes('exists')) {
        if (currentUser) setAlreadySubmitted(true);
        else setGuestAlreadyJoined(true);
        setError("You've already submitted the interest form. We'll be in touch when we launch!");
      } else {
        setError(msg || 'Something went wrong. Please try again.');
      }
    } finally { setLoading(false); }
  };

  const handlePaystackPayment = async () => {
    setError('');
    if (!form.fullName || form.fullName.trim().length < 2) { setError('Please enter your full name before proceeding to payment.'); scrollToForm(); return; }
    if (!form.phone || form.phone.trim().length < 7) { setError('Please enter a valid phone number before proceeding to payment.'); scrollToForm(); return; }
    if (!form.email || !form.email.includes('@')) { setError('Please enter a valid email address before proceeding to payment.'); scrollToForm(); return; }
    if (!form.location || form.location.trim().length < 2) { setError('Please enter your preferred location before proceeding to payment.'); scrollToForm(); return; }
    if (!paystackReady || !window.PaystackPop) {
      const ok = await loadPaystackScript();
      if (ok) { setPaystackReady(true); } else { setError('Payment gateway could not be loaded. Please check your internet connection and try again.'); return; }
    }
    try {
      const handler = window.PaystackPop.setup({
        key: PAYSTACK_PUBLIC_KEY, email: form.email.trim(),
        amount: WAITLIST_FEE * 100, currency: 'NGN',
        ref: `literent_priority_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        label: form.fullName.trim(),
        channels: ['card', 'bank', 'ussd', 'qr', 'mobile_money', 'bank_transfer'],
        metadata: {
          custom_fields: [
            { display_name: 'Full Name', variable_name: 'full_name', value: form.fullName },
            { display_name: 'Phone', variable_name: 'phone', value: form.phone },
            { display_name: 'Location', variable_name: 'location', value: form.location },
            { display_name: 'Property', variable_name: 'house', value: house.title || 'General Interest Form' },
            { display_name: 'Address', variable_name: 'address', value: house.address || house.location || 'N/A' },
            { display_name: 'Fee Type', variable_name: 'fee_type', value: 'Priority Interest Fee' },
          ],
        },
        callback: (response) => { submitToBackend({ paystackRef: response.reference, feePaid: true }); },
        onClose: () => { setError('Payment window was closed. You can try again or submit the free interest form instead.'); setLoading(false); },
      });
      handler.openIframe();
    } catch (err) { setError('Could not open payment window. Please refresh the page and try again.'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (currentUser && alreadySubmitted) return;
    if (payPriorityFee) { handlePaystackPayment(); } else { await submitToBackend({ feePaid: false }); }
  };

  const isBlocked = (currentUser && alreadySubmitted === true) || (!currentUser && guestAlreadyJoined);
  const isDisabled = isBlocked || loading || checkingStatus || checkingGuest;
  const houseAddress = house.address || house.location || '';

  const faqs = [
    { q: 'Why is there a waitlist — when is LiteRent launching?', a: "LiteRent is in its final pre-launch phase! We're putting finishing touches on the platform and curating our first set of verified properties in Ibadan. By joining the waitlist now, you'll be among the very first people to get access the moment we go live." },
    { q: 'What happens after I join the waitlist?', a: "You'll receive a confirmation on this screen. Once LiteRent officially launches, you'll be notified immediately — before the general public — and can browse and apply for homes straight away." },
    { q: 'How does the ₦20,000 Priority Fee work?', a: "Paying the optional ₦20,000 fee places you at the very top of our launch queue and qualifies you for an exclusive discount on your first rent payment when we go live. Payment is processed instantly and securely via Paystack. It's fully refundable if we launch and you don't find a suitable home within 30 days. It is also part of your initial rent deposit when we launch." },
    { q: 'Is joining the waitlist free?', a: "Yes! The standard waitlist is completely free. The ₦20,000 Priority Fee is optional and gives you top-of-queue privileges plus a rent discount at launch. It is refundable and part of your initial deposit when we launch." },
    { q: 'Can I join for a specific property?', a: "Absolutely. If you came from a listing page, your interest in that specific property is already recorded. You can also join the general waitlist to be notified about any great home that matches your preferences." },
    { q: 'How will I be notified at launch?', a: 'We will reach out via the phone number and email address you provide. Make sure both are correct and reachable.' },
    { q: 'Is LiteRent a legitimate platform?', a: "Yes. LiteRent is a registered Nigerian property rental platform built to make renting homes transparent, simple, and fair. All properties listed will be physically verified before going live." },
  ];

  // ── SUCCESS SCREEN ────────────────────────────────────────────────────────
  if (success) {
    return (
      <div className="wl-root" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f1f38 0%, #1a3250 50%, #294c79 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ maxWidth: 520, width: '100%', background: '#fff', borderRadius: 28, padding: '56px 48px', textAlign: 'center', boxShadow: '0 32px 80px rgba(0,0,0,0.3)' }}>
          <div className="wl-float" style={{ fontSize: 72, marginBottom: 24 }}>🎉</div>
          <h2 className="wl-serif" style={{ fontSize: 36, color: '#1a2540', marginBottom: 16, lineHeight: 1.2 }}>You're on the List!</h2>
          <p style={{ color: '#5a6b7e', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
            Welcome, <strong style={{ color: '#294c79' }}>{successData.name}</strong>! You're now on LiteRent's
            pre-launch waitlist. We'll reach out to you at <strong style={{ color: '#294c79' }}>{successData.phone}</strong> the
            moment we go live.
          </p>
          {successData.feePaid && (
            <div style={{ background: 'linear-gradient(135deg, #e8f2d1, #f0f7e6)', border: '1.5px solid #a5c250', borderRadius: 16, padding: '16px 20px', marginBottom: 24, color: '#2d5a0e', fontWeight: 600, fontSize: 14 }}>
              ⭐ Priority status confirmed — you're at the top of our launch queue with a rent discount secured!
            </div>
          )}
          <p style={{ color: '#b0bac9', fontSize: 13, marginBottom: 28 }}>Redirecting you to the homepage in a few seconds…</p>
          <Link to="/" style={{ display: 'inline-block', background: 'linear-gradient(135deg, #a5c250, #7da83a)', color: '#fff', padding: '14px 40px', borderRadius: 14, fontWeight: 700, textDecoration: 'none', fontSize: 16, boxShadow: '0 4px 20px rgba(165,194,80,0.4)' }}>
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  // ── ALREADY SUBMITTED SCREEN ──────────────────────────────────────────────
  if (currentUser && alreadySubmitted === true && !checkingStatus) {
    return (
      <div className="wl-root" style={{ minHeight: '100vh', background: '#f4f7fc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ maxWidth: 520, width: '100%', background: '#fff', borderRadius: 28, padding: '48px 40px', textAlign: 'center', boxShadow: '0 8px 40px rgba(41,76,121,0.12)', border: '1.5px solid #eef2f8' }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, #e8f2d1, #c8dfa0)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#5a9a1a" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
          </div>
          <h2 className="wl-serif" style={{ fontSize: 30, color: '#1a2540', marginBottom: 12 }}>Already on the List!</h2>
          <p style={{ color: '#5a6b7e', lineHeight: 1.7, marginBottom: 8 }}>
            Hi <strong style={{ color: '#294c79' }}>{currentUser.full_name || currentUser.fullName || currentUser.name || 'there'}</strong> — we already have your details on our pre-launch waitlist.
          </p>
          <p style={{ color: '#7a8fa6', fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
            You'll be notified at <strong>{currentUser.phone || currentUser.email || 'your contact'}</strong> the moment LiteRent goes live.
            {house.title && <> Your interest in <strong style={{ color: '#294c79' }}>{house.title}</strong> has been recorded.</>}
          </p>
          <div style={{ background: '#fffbeb', border: '1.5px solid #f6d860', borderRadius: 16, padding: '16px 20px', marginBottom: 28, textAlign: 'left' }}>
            <p style={{ fontWeight: 700, color: '#92400e', fontSize: 13, marginBottom: 8 }}>📋 What happens next?</p>
            <ul style={{ color: '#78350f', fontSize: 13, lineHeight: 2, paddingLeft: 18 }}>
              <li>We'll contact you as soon as LiteRent launches</li>
              <li>You'll get first pick of verified properties</li>
              <li>Priority members get contacted before other members</li>
            </ul>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Link to="/" className="wl-btn-primary" style={{ flex: 1, padding: '13px 20px', borderRadius: 14, textDecoration: 'none', textAlign: 'center', display: 'block' }}>Back to Home</Link>
            <Link to="/browse" style={{ flex: 1, padding: '13px 20px', borderRadius: 14, border: '2px solid #294c79', color: '#294c79', fontWeight: 700, textDecoration: 'none', textAlign: 'center', display: 'block', transition: 'all 0.2s', fontFamily: 'DM Sans, sans-serif' }}>Browse Listings</Link>
          </div>
        </div>
      </div>
    );
  }

  // ── MAIN PAGE ─────────────────────────────────────────────────────────────
  return (
    <>
      <SEOHead title="Join the LiteRent Waitlist | 30% Upfront Homes in Ibadan" description="Join the LiteRent waitlist for early access to verified homes in Ibadan with 30% upfront and the balance spread over time." canonical="https://literent.com.ng/waitlist" />
      <div className="wl-root" style={{ minHeight: '100vh', background: '#f4f7fc' }}>
      {/* ══ HEADER ══ */}
      <header className="sticky top-0 z-50 bg-white border-b border-[#afc09e] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex-shrink-0">
              <img src="/Logo.jpg" alt="LiteRent Logo" className="h-12 sm:h-14 md:h-16 w-auto object-contain"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/180x60?text=LiteRent'; }} />
            </Link>
            <nav className="hidden lg:flex items-center space-x-8 xl:space-x-10">
              {[
                { to: '/', label: 'Home' },
                { to: '/how-it-works', label: 'How It Works' },
                { to: '/browse', label: 'Browse Houses' },
                { to: '/about', label: 'About Us' },
                { to: '/contact', label: 'Contact Us' },
                { to: '/waitlist', label: 'Waitlist', active: true },
              ].map((link) => (
                <Link key={link.to} to={link.to}
                  className={`transition-colors font-medium ${link.active ? 'text-[#a5c250] font-semibold' : 'text-[#294c79] hover:text-[#1f3a5c]'}`}>
                  {link.label}
                </Link>
              ))}
              <div className="flex items-center space-x-4">
                {(isLoggedIn || isAdminLoggedIn) ? (
                  <>
                    <Link to={getDashboardLink()}>
                      <button className={`px-6 py-2.5 text-white rounded-full font-medium transition shadow-sm ${isAdminLoggedIn ? 'bg-red-600 hover:bg-red-700' : 'bg-[#294c79] hover:bg-[#1f3a5c]'}`}>
                        {getDashboardLabel()}
                      </button>
                    </Link>
                    <button onClick={handleLogout} className="px-6 py-2.5 border-2 border-red-500 text-red-600 rounded-full font-medium hover:bg-red-50 transition">Logout</button>
                  </>
                ) : (
                  <>
                    <Link to="/login"><button className="px-6 py-2.5 border-2 border-[#294c79] text-[#294c79] rounded-full font-medium hover:bg-[#294c79]/10 transition">Login</button></Link>
                    <Link to="/signup"><button className="px-6 py-2.5 bg-[#a5c250] text-white rounded-full font-medium hover:bg-[#8bb83a] shadow-sm transition">Get Started</button></Link>
                  </>
                )}
              </div>
            </nav>
            <button className="lg:hidden p-2 rounded-md text-[#294c79] hover:bg-gray-100 focus:outline-none"
              onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu">
              <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
              </svg>
            </button>
          </div>
        </div>
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t border-[#afc09e] py-6 px-5">
            <div className="flex flex-col space-y-5">
              {[
                { to: '/', label: 'Home' },
                { to: '/how-it-works', label: 'How It Works' },
                { to: '/browse', label: 'Browse Houses' },
                { to: '/about', label: 'About Us' },
                { to: '/contact', label: 'Contact Us' },
                { to: '/waitlist', label: 'Waitlist', active: true },
              ].map((link) => (
                <Link key={link.to} to={link.to}
                  className={`py-3 text-lg font-medium ${link.active ? 'text-[#a5c250] font-semibold' : 'text-[#294c79]'}`}
                  onClick={() => setIsMenuOpen(false)}>
                  {link.label}
                </Link>
              ))}
              <div className="pt-6 border-t border-gray-200 flex flex-col space-y-4">
                {(isLoggedIn || isAdminLoggedIn) ? (
                  <>
                    <Link to={getDashboardLink()} onClick={() => setIsMenuOpen(false)}>
                      <button className={`w-full py-4 text-white rounded-full font-medium text-lg ${isAdminLoggedIn ? 'bg-red-600' : 'bg-[#294c79]'}`}>{getDashboardLabel()}</button>
                    </Link>
                    <button onClick={() => { handleLogout(); setIsMenuOpen(false); }} className="w-full py-4 border-2 border-red-500 text-red-600 rounded-full font-medium text-lg">Logout</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setIsMenuOpen(false)}><button className="w-full py-4 border-2 border-[#294c79] text-[#294c79] rounded-full font-medium text-lg">Login</button></Link>
                    <Link to="/signup" onClick={() => setIsMenuOpen(false)}><button className="w-full py-4 bg-[#a5c250] text-white rounded-full font-medium text-lg">Get Started</button></Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ══ HERO ══ */}
      <div style={{ position: 'relative', background: 'linear-gradient(145deg, #0d1e38 0%, #1a3254 40%, #294c79 100%)', overflow: 'hidden' }}>
        <div className="wl-noise" />
        {/* Decorative circles */}
        <div style={{ position: 'absolute', top: -120, right: -80, width: 480, height: 480, borderRadius: '50%', background: 'radial-gradient(circle, rgba(165,194,80,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -80, left: -60, width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div style={{ maxWidth: 1080, margin: '0 auto', padding: '64px 24px 72px' }}>
          {/* Live badge */}
          <div className="wl-fade-up-1" style={{ display: 'inline-flex', alignItems: 'center', background: 'rgba(165,194,80,0.15)', border: '1px solid rgba(165,194,80,0.35)', borderRadius: 100, padding: '8px 18px', marginBottom: 36, fontSize: 13, fontWeight: 600, color: '#c8e87a' }}>
            <span className="wl-live-dot" />
            LiteRent is launching soon — secure your spot today
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 48, alignItems: 'center' }}>
            <div>
              <h1 className="wl-fade-up-2 wl-serif" style={{ fontSize: 'clamp(36px, 5vw, 58px)', color: '#fff', lineHeight: 1.15, marginBottom: 20 }}>
                Great Homes Are<br />
                <span className="wl-shimmer-text">Coming to Ibadan</span>
              </h1>
              <p className="wl-fade-up-3" style={{ color: 'rgba(255,255,255,0.72)', fontSize: 17, lineHeight: 1.75, marginBottom: 28 }}>
                LiteRent is Ibadan's upcoming home rental platform — built to make renting
                transparent, simple, and fair. Join our pre-launch waitlist now and be the{' '}
                <strong style={{ color: '#fff' }}>very first</strong> to access verified homes the moment we go live.
              </p>
              <div className="wl-fade-up-3" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 32 }}>
                {['✅ Verified listings', '🔒 Transparent renting', '⚡ Early access', '💳 Flexible payments'].map(t => (
                  <span key={t} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', color: 'rgba(255,255,255,0.85)', fontSize: 13, padding: '7px 14px', borderRadius: 100 }}>{t}</span>
                ))}
              </div>
              <button onClick={scrollToForm} className="wl-fade-up-4 wl-btn-primary" style={{ padding: '16px 36px', fontSize: 17, borderRadius: 16 }}>
                Join the Waitlist →
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <StatPill icon="🏠" value="100+" label="Homes being verified" />
              <StatPill icon="📍" value="Ibadan" label="City at launch" />
              <StatPill icon="🚀" value="Soon™" label="Launching very soon" />
              <StatPill icon="🎁" value="Priority" label="Exclusive discounts" />
            </div>
          </div>
        </div>
      </div>

      {/* ══ WHAT IS LITERENT ══ */}
      <div style={{ background: '#fff', borderBottom: '1px solid #eef2f8', padding: '72px 24px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={{ display: 'inline-block', background: '#e8f2d1', color: '#3a6b14', fontSize: 12, fontWeight: 700, padding: '6px 16px', borderRadius: 100, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>About LiteRent</div>
            <h2 className="wl-serif" style={{ fontSize: 'clamp(28px, 4vw, 40px)', color: '#1a2540', marginBottom: 14 }}>What is LiteRent?</h2>
            <p style={{ color: '#7a8fa6', maxWidth: 580, margin: '0 auto', lineHeight: 1.75, fontSize: 16 }}>
              LiteRent is a new kind of rental platform for Ibadan — one that puts tenants first.
              Every property is physically inspected before listing. No fake photos, no hidden fees, no stress.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18 }}>
            <FeatureTile icon="🔍" title="Verified Listings Only" desc="Every property will be physically inspected and confirmed before it goes live. What you see is what you get." />
            <FeatureTile icon="💳" title="Flexible Payment Plans" desc="Monthly instalment options — easing the pressure on tenants so you never need to pay a lump sum." />
            <FeatureTile icon="⚖️" title="Fair, Legal Agreements" desc="All tenancy agreements are straightforward, tenant-friendly, and reviewed for fairness before signing." />
            <FeatureTile icon="📲" title="Real-time Notifications" desc="When a home matching your preferences goes live, you'll be notified instantly — before the public." />
            <FeatureTile icon="🛡️" title="No Hidden Fees" desc="What you see is what you pay. LiteRent is built on transparency — every cost is disclosed upfront." />
            <FeatureTile icon="⭐" title="Priority Queue System" desc="Pay the optional ₦20,000 fee to jump to the front of the launch queue and unlock an exclusive rent discount." />
          </div>
        </div>
      </div>

      {/* ══ HOW IT WORKS ══ */}
      <div style={{ padding: '72px 24px', background: '#f4f7fc' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 className="wl-serif" style={{ fontSize: 'clamp(26px, 4vw, 38px)', color: '#1a2540', marginBottom: 12 }}>How the Waitlist Works</h2>
            <p style={{ color: '#7a8fa6', fontSize: 15 }}>Four simple steps from registration to getting your keys.</p>
          </div>
          <div style={{ background: '#fff', borderRadius: 24, padding: '36px 40px', boxShadow: '0 4px 24px rgba(41,76,121,0.08)', border: '1.5px solid #eef2f8' }}>
            <StepItem num="1" title="Fill the form below" desc="Tell us your name, contact details, preferred location, and budget. Takes under 2 minutes." />
            <StepItem num="2" title="Optional: pay the ₦20,000 Priority Fee" desc="Lock in your top-of-queue spot and an exclusive launch discount via Paystack — secure, instant, and refundable." />
            <StepItem num="3" title="We notify you at launch" desc="The moment LiteRent goes live, you'll be contacted directly — before anyone else — so you can browse and apply first." />
            <StepItem num="4" title="Move into your verified home" desc="Pick your home, complete your documentation, and get your keys. Simple and transparent from start to finish." last />
          </div>
        </div>
      </div>

      {/* ══ PRIORITY FEE HIGHLIGHT ══ */}
      <div style={{ background: 'linear-gradient(135deg, #1a3250, #294c79)', padding: '72px 24px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 48, alignItems: 'center' }}>
            <div style={{ color: '#fff' }}>
              <div style={{ display: 'inline-block', background: '#a5c250', color: '#fff', fontSize: 11, fontWeight: 800, padding: '5px 14px', borderRadius: 100, marginBottom: 20, textTransform: 'uppercase', letterSpacing: 1.5 }}>Optional Priority Upgrade</div>
              <h3 className="wl-serif" style={{ fontSize: 'clamp(26px, 4vw, 38px)', marginBottom: 18, lineHeight: 1.25 }}>Be First in Line at Launch</h3>
              <p style={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.8, marginBottom: 20, fontSize: 15 }}>
                For just <span style={{ color: '#a5c250', fontWeight: 800, fontSize: 22 }}>₦20,000</span>, you get
                placed at the very top of our launch waitlist and receive an exclusive discount off
                your first rent payment. It is also part of your initial rent deposit when we Launch.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  '🥇 First-contact guarantee when we launch',
                  '💰 Exclusive discount on your first rent payment',
                  '🔁 100% refundable if no match is found within 30 days',
                  '🔒 Paid securely via Paystack — card, transfer, USSD',
                ].map(i => <li key={i} style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, paddingBottom: 12, lineHeight: 1.5 }}>{i}</li>)}
              </ul>
            </div>
            <div style={{ background: '#fff', borderRadius: 24, padding: '36px 32px', textAlign: 'center', boxShadow: '0 16px 48px rgba(0,0,0,0.25)' }}>
              <div style={{ fontSize: 52, marginBottom: 8 }}>⭐</div>
              <p style={{ color: '#b0bac9', fontSize: 13, marginBottom: 4 }}>One-time optional fee</p>
              <p className="wl-serif" style={{ fontSize: 48, color: '#1a2540', fontWeight: 900, lineHeight: 1, marginBottom: 6 }}>₦20,000</p>
              <p style={{ color: '#a5c250', fontWeight: 700, fontSize: 13, marginBottom: 24 }}>Refundable within 30 days of launch</p>
              <button onClick={scrollToForm} className="wl-btn-primary" style={{ width: '100%', padding: '15px', fontSize: 16, borderRadius: 14, display: 'block' }}>
                Get Priority Access →
              </button>
              <p style={{ color: '#b0bac9', fontSize: 12, marginTop: 12 }}>Processed securely by Paystack</p>
            </div>
          </div>
        </div>
      </div>

      {/* ══ FAQ ══ */}
      <div style={{ background: '#fff', padding: '72px 24px' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 className="wl-serif" style={{ fontSize: 'clamp(26px, 4vw, 38px)', color: '#1a2540', marginBottom: 12 }}>Frequently Asked Questions</h2>
            <p style={{ color: '#7a8fa6', fontSize: 15 }}>Everything you need to know before joining.</p>
          </div>
          {faqs.map((faq, i) => (
            <FAQItem key={i} q={faq.q} a={faq.a} open={openFAQ === i} toggle={() => setOpenFAQ(openFAQ === i ? null : i)} />
          ))}
        </div>
      </div>

      {/* ══ CTA BAND ══ */}
      <div style={{ background: 'linear-gradient(135deg, #e8f2d1, #d4eda0)', padding: '52px 24px', textAlign: 'center', borderTop: '2px solid #c8dfa0', borderBottom: '2px solid #c8dfa0' }}>
        <p className="wl-serif" style={{ color: '#1a3210', fontSize: 'clamp(20px, 3vw, 28px)', marginBottom: 20 }}>Ready to secure your spot before launch?</p>
        <button onClick={scrollToForm} className="wl-btn-dark" style={{ padding: '16px 44px', fontSize: 17, borderRadius: 16 }}>
          Fill the Waitlist Form ↓
        </button>
        <p style={{ color: '#5a7a30', fontSize: 13, marginTop: 14 }}>Takes less than 2 minutes • No commitment required</p>
      </div>

      {/* ══ FORM ══ */}
      <div ref={formRef} style={{ padding: '72px 24px', background: '#f4f7fc' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>

          {currentUser && checkingStatus && (
            <div style={{ marginBottom: 24, background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: 16, padding: 18, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="wl-spin" style={{ width: 20, height: 20, border: '2.5px solid #294c79', borderTopColor: 'transparent', borderRadius: '50%', flexShrink: 0 }} />
              <p style={{ color: '#1e3a6e', fontSize: 14, fontWeight: 600 }}>Checking your waitlist status…</p>
            </div>
          )}

          {!currentUser && guestAlreadyJoined && (
            <div style={{ marginBottom: 24, background: '#fffbeb', border: '1.5px solid #f6d860', borderRadius: 16, padding: '18px 22px', textAlign: 'center' }}>
              <p style={{ fontWeight: 700, color: '#92400e', fontSize: 16, marginBottom: 4 }}>⚠️ You're already on our list!</p>
              <p style={{ color: '#78350f', fontSize: 14 }}>We already have your details{house.title ? ` for ${house.title}` : ''}. We'll reach out at launch — no need to submit again.</p>
            </div>
          )}

          {/* Form card */}
          <div style={{ background: '#fff', borderRadius: 28, boxShadow: '0 8px 48px rgba(41,76,121,0.12)', border: '1.5px solid #eef2f8', overflow: 'hidden' }}>

            {/* Form header */}
            <div style={{ background: 'linear-gradient(145deg, #0d1e38, #1a3254, #294c79)', padding: '44px 40px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
              <div className="wl-noise" />
              <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(165,194,80,0.2) 0%, transparent 70%)', pointerEvents: 'none' }} />
              <div style={{ display: 'inline-flex', alignItems: 'center', background: 'rgba(165,194,80,0.2)', border: '1px solid rgba(165,194,80,0.4)', borderRadius: 100, padding: '7px 16px', marginBottom: 20, fontSize: 12, fontWeight: 700, color: '#c8e87a' }}>
                <span className="wl-live-dot" />
                Pre-launch waitlist is open
              </div>
              <h2 className="wl-serif" style={{ fontSize: 36, color: '#fff', marginBottom: 10 }}>Join the Waitlist</h2>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 15 }}>
                {house.title
                  ? <>Interested in <span style={{ color: '#a5c250', fontWeight: 600 }}>{house.title}</span></>
                  : 'Be first to access LiteRent when we launch'}
              </p>
              {currentUser && (
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, marginTop: 8 }}>
                  Signed in as <strong style={{ color: 'rgba(255,255,255,0.75)' }}>{currentUser.full_name || currentUser.email}</strong>
                </p>
              )}
            </div>

            <div style={{ padding: '40px 40px 44px' }}>
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                {/* Full Name */}
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#294c79', marginBottom: 8, letterSpacing: 0.3 }}>
                    Full Name <span style={{ color: '#e74c3c' }}>*</span>
                  </label>
                  <input type="text" name="fullName" value={form.fullName} onChange={handleChange}
                    required disabled={isDisabled} placeholder="e.g. Amaka Okafor" className="wl-input" />
                </div>

                {/* Phone + Email */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#294c79', marginBottom: 8 }}>
                      Phone Number <span style={{ color: '#e74c3c' }}>*</span>
                    </label>
                    <input type="tel" name="phone" value={form.phone} onChange={handleChange}
                      onBlur={handleBlurCheck} required disabled={isDisabled}
                      placeholder="+234 801 234 5678" className="wl-input" />
                    {checkingGuest && <p style={{ fontSize: 12, color: '#b0bac9', marginTop: 4 }}>Checking…</p>}
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#294c79', marginBottom: 8 }}>
                      Email Address <span style={{ color: '#e74c3c' }}>*</span>
                    </label>
                    <input type="email" name="email" value={form.email} onChange={handleChange}
                      onBlur={handleBlurCheck} required disabled={isDisabled}
                      placeholder="you@example.com" className="wl-input" />
                  </div>
                </div>

                {/* Location */}
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#294c79', marginBottom: 8 }}>
                    Preferred Location / Area <span style={{ color: '#e74c3c' }}>*</span>
                  </label>
                  <input type="text" name="location" value={form.location} onChange={handleChange}
                    required disabled={isDisabled} placeholder="e.g. Bodija, Ring Road, Jericho…" className="wl-input" />
                  <p style={{ fontSize: 12, color: '#b0bac9', marginTop: 6 }}>
                    {houseAddress ? 'Pre-filled from the property address — you can edit this if needed.' : 'Which neighbourhood in Ibadan are you targeting?'}
                  </p>
                </div>

                {/* Property of interest */}
                {house.title && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#294c79', marginBottom: 8 }}>Property of Interest</label>
                      <input type="text" value={house.title} readOnly
                        style={{ width: '100%', padding: '14px 18px', border: '2px solid rgba(165,194,80,0.5)', borderRadius: 14, background: '#f6fbee', color: '#294c79', fontWeight: 600, fontFamily: 'DM Sans, sans-serif', fontSize: 15, cursor: 'default', boxSizing: 'border-box' }} />
                    </div>
                    {houseAddress && (
                      <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#294c79', marginBottom: 8 }}>Property Address</label>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '14px 18px', background: '#f6fbee', border: '2px solid rgba(165,194,80,0.5)', borderRadius: 14 }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#a5c250" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                          <span style={{ color: '#294c79', fontWeight: 600, fontSize: 14, lineHeight: 1.5 }}>{houseAddress}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Move-in + Budget */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#294c79', marginBottom: 8 }}>Preferred Move-in Date</label>
                    <input type="date" name="moveInDate" value={form.moveInDate} onChange={handleChange}
                      disabled={isDisabled} min={new Date().toISOString().split('T')[0]} className="wl-input" />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#294c79', marginBottom: 8 }}>Annual Budget (₦)</label>
                    <input type="number" name="budget" value={form.budget} onChange={handleChange}
                      disabled={isDisabled} placeholder="e.g. 1,500,000" className="wl-input" />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#294c79', marginBottom: 8 }}>Additional Message</label>
                  <textarea name="message" value={form.message} onChange={handleChange}
                    rows="3" disabled={isDisabled}
                    style={{ width: '100%', padding: '14px 18px', border: '2px solid #e8edf5', borderRadius: 14, fontFamily: 'DM Sans, sans-serif', fontSize: 15, color: '#1a2540', background: isDisabled ? '#f4f6fa' : '#fafbfd', resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.6, transition: 'all 0.2s' }} />
                </div>

                {/* Priority fee toggle */}
                <div
                  onClick={() => !isDisabled && setPayPriorityFee(v => !v)}
                  style={{
                    borderRadius: 18, border: `2px solid ${isDisabled ? '#eee' : payPriorityFee ? '#a5c250' : '#e8edf5'}`,
                    padding: 20, cursor: isDisabled ? 'default' : 'pointer',
                    background: isDisabled ? '#f8f9fb' : payPriorityFee ? 'linear-gradient(135deg, #f6fbee, #edf6d6)' : '#fafbfd',
                    opacity: isDisabled ? 0.6 : 1, transition: 'all 0.2s',
                  }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 14, cursor: isDisabled ? 'default' : 'pointer' }} onClick={e => e.stopPropagation()}>
                    <div style={{ flexShrink: 0, marginTop: 2 }}>
                      <input type="checkbox" style={{ display: 'none' }} checked={payPriorityFee} disabled={isDisabled} onChange={e => setPayPriorityFee(e.target.checked)} />
                      <div style={{ width: 24, height: 24, borderRadius: 8, border: `2.5px solid ${payPriorityFee ? '#a5c250' : '#c8d4e0'}`, background: payPriorityFee ? '#a5c250' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
                        {payPriorityFee && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>}
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{ fontWeight: 800, color: '#1a2540', fontSize: 15 }}>Pay ₦20,000 Priority Fee</span>
                        <span style={{ background: '#a5c250', color: '#fff', fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 100 }}>OPTIONAL</span>
                        <span style={{ background: '#fff3e0', color: '#e65100', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 100 }}>LAUNCH DISCOUNT</span>
                      </div>
                      <p style={{ color: '#7a8fa6', fontSize: 13.5, lineHeight: 1.65, margin: 0 }}>
                        Jump to the front of our launch queue and receive an{' '}
                        <strong style={{ color: '#294c79' }}>exclusive discount</strong> on your first rent.
                        Paid securely via Paystack. Refundable within 90 days of launch if no match found.
                      </p>
                      {payPriorityFee && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                          {['💳 Card', '🏦 Bank Transfer', '📱 USSD', '📲 Mobile Money'].map(m => (
                            <span key={m} style={{ background: '#fff', border: '1.5px solid #a5c250', color: '#294c79', fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 8 }}>{m}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </label>
                </div>

                {payPriorityFee && !isDisabled && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#b0bac9', fontSize: 12 }}>
                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
                    <span>Payment secured by <strong style={{ color: '#8a9bb0' }}>Paystack</strong> — PCI-DSS compliant &amp; trusted across Nigeria</span>
                  </div>
                )}

                {error && (
                  <div style={{ background: '#fff5f5', border: '1.5px solid #fed7d7', borderRadius: 14, padding: '14px 18px', color: '#c53030', fontSize: 14, fontWeight: 500, textAlign: 'center' }}>
                    ⚠️ {error}
                  </div>
                )}

                {/* Submit */}
                <button type="submit" disabled={isDisabled}
                  className={isBlocked ? '' : payPriorityFee ? 'wl-btn-dark' : 'wl-btn-primary'}
                  style={{
                    width: '100%', padding: '18px', fontSize: 17, borderRadius: 16,
                    ...(isBlocked ? { background: '#eef2f8', color: '#b0bac9', cursor: 'not-allowed', border: 'none', fontFamily: 'DM Sans, sans-serif', fontWeight: 700 } : {}),
                  }}>
                  {checkingStatus ? '⏳ Checking your status…'
                    : loading ? '⏳ Processing…'
                    : isBlocked ? '✓ Already on the Waitlist'
                    : payPriorityFee ? `Pay ₦${WAITLIST_FEE.toLocaleString()} & Get Priority Access`
                    : 'Join Waitlist — Free'}
                </button>

                <p style={{ textAlign: 'center', color: '#b0bac9', fontSize: 12, lineHeight: 1.6 }}>
                  By submitting, you agree to be contacted by LiteRent via call or WhatsApp when we launch.
                  {payPriorityFee && ' Your payment will be processed securely by Paystack.'}
                </p>
              </form>
            </div>
          </div>

          {/* Trust strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginTop: 24 }}>
            {[{ icon: '🔒', label: 'SSL Secured' }, { icon: '✅', label: 'Verified Platform' }, { icon: '🔁', label: 'Refundable Fee' }].map(t => (
              <div key={t.label} style={{ background: '#fff', borderRadius: 16, padding: '18px 12px', textAlign: 'center', boxShadow: '0 2px 12px rgba(41,76,121,0.07)', border: '1.5px solid #eef2f8' }}>
                <div style={{ fontSize: 24, marginBottom: 6 }}>{t.icon}</div>
                <p style={{ fontSize: 12, color: '#7a8fa6', fontWeight: 600, margin: 0 }}>{t.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
      </div>
    </>
  );
}
